import { config } from "@nova/configuration/browser"
import { SupportedRegions } from "@nova/constants"
import { PdfLogo, PdfLogoSettings, pdfLogoSettings } from "@nova/schemas"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

export const PDF_NOVA_LOGO_VALUE = "__nova__"
export const PDF_NONE_LOGO_VALUE = "__none__"
export const PDF_NOVA_LOGO_SRC = "/images/logos/nova-logo-with-title-dark.svg"

export const parsePdfLogoSettings = (value: string | undefined | null): PdfLogoSettings => {
    try {
        return pdfLogoSettings.parse(JSON.parse(value || "{}"))
    } catch {
        return {}
    }
}

/** Reads the workspace PDF logo library from configuration (non-React contexts). */
export const getPdfLogoSettings = (): PdfLogoSettings =>
    parsePdfLogoSettings(config.get("pdfLogos"))

export const pdfLogoAssetUrl = (workspaceId: string, logoId: string, region?: SupportedRegions) =>
    fileManagementApi.getPublicAssetUrl(`public/org/${workspaceId}/assets/${logoId}`, { region })

export const isCrossOriginLogoSrc = (src: string) =>
    !!src &&
    !src.startsWith("data:") &&
    new URL(src, window.location.href).origin !== window.location.origin

const isCustomLogo = (logo?: PdfLogo) =>
    !!logo && logo.id !== PDF_NOVA_LOGO_VALUE && logo.id !== PDF_NONE_LOGO_VALUE

export const hasCustomWorkspaceLogo = (settings: PdfLogoSettings): boolean =>
    isCustomLogo(settings.leftLogo) || isCustomLogo(settings.rightLogo)

/**
 * Resolves a logo slot's setting to an image src ("" means no logo):
 * - not configured (undefined) -> left falls back to Nova, right to no logo
 * - the "none" sentinel        -> no logo (empty)
 * - the Nova sentinel          -> the built-in Nova logo
 * - an uploaded logo id        -> that logo asset (empty if no workspaceId)
 */
export const resolvePdfLogoSrc = (
    logo: PdfLogo | undefined,
    side: "left" | "right",
    workspaceId: string
): string => {
    if (logo === undefined) return side === "left" ? PDF_NOVA_LOGO_SRC : ""
    if (logo.id === PDF_NONE_LOGO_VALUE) return ""
    if (logo.id === PDF_NOVA_LOGO_VALUE) return PDF_NOVA_LOGO_SRC
    return workspaceId ? pdfLogoAssetUrl(workspaceId, logo.id) : ""
}

/** Resolves the workspace's configured default logo for a slot, to seed a PDF. */
export const resolveDefaultPdfLogoSrc = (side: "left" | "right", workspaceId: string): string => {
    const settings = getPdfLogoSettings()
    return resolvePdfLogoSrc(
        side === "left" ? settings.leftLogo : settings.rightLogo,
        side,
        workspaceId
    )
}
